import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, OnInit, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { marker as extractTranslation } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateModule } from '@ngx-translate/core';
import { SENSITIVE_DATA_API_SERVICE_TOKEN } from '@nk/api-lib';
import { SensitiveDataCompanyAccessEntity, SensitiveDataWatchlistAccessEntity } from '@nk/data-entities-lib';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { getTogglePaymentRemarksModalProps, ToggleAction } from '../../utils/payment-remarks.utils';
import { TogglePaymentRemarksModalComponent } from '../toggle-payment-remarks-modal/toggle-payment-remarks-modal.component';

@Component({
  selector: 'nk-payment-remarks-section',
  templateUrl: './payment-remarks-section.component.html',
  imports: [AsyncPipe, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PaymentRemarksSectionComponent implements OnInit {
  public watchlistUuid = input<string>();
  public companyUuid = input<string>();
  public enableMessage = input<string>(extractTranslation('company.payment_remarks.payment_remarks_enable_message'));
  public sensitiveDataAccessEntityUuid$!: Observable<string | null | undefined>;
  public isLoading = false;
  public accessGranted = output<void>();

  private _triggerSensitiveDataAccess$ = new BehaviorSubject<
    SensitiveDataWatchlistAccessEntity | SensitiveDataCompanyAccessEntity | null
  >(null);
  private _sensitiveDataService = inject(SENSITIVE_DATA_API_SERVICE_TOKEN);
  private _modalService = inject(BsModalService);
  private _destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.sensitiveDataAccessEntityUuid$ = this._triggerSensitiveDataAccess$.pipe(
      switchMap(() =>
        this._getSensitiveDataAccessEntity({
          watchlistUuid: this.watchlistUuid(),
          companyUuid: this.companyUuid(),
        }),
      ),
      map((entity) => (entity ? entity.uuid : null)),
    );
  }

  private _getSensitiveDataAccessEntity({
    watchlistUuid,
    companyUuid,
  }: {
    companyUuid?: string;
    watchlistUuid?: string;
  }):
    | Observable<SensitiveDataWatchlistAccessEntity | undefined>
    | Observable<SensitiveDataCompanyAccessEntity | undefined> {
    if (watchlistUuid) {
      return this._sensitiveDataService.sensitiveDataWatchlistAccessEntityListCache
        .get()
        .pipe(map((entities) => entities.find((i) => i.watchlist_uuid === watchlistUuid)));
    }
    if (companyUuid) {
      return this._sensitiveDataService.sensitiveDataCompanyAccessEntityListCache
        .get()
        .pipe(map((entities) => entities.find((i) => i.company_uuid === companyUuid)));
    }
    return throwError(() => new Error('No valid SensitiveDataAccessEntity uuid provided'));
  }

  public openTogglePaymentRemarksModal(action: ToggleAction, type: 'watchlist' | 'company', uuid: string): void {
    const initialState = getTogglePaymentRemarksModalProps(action, type, uuid);
    const modalRef = this._modalService.show(TogglePaymentRemarksModalComponent, { initialState });
    const content = modalRef.content as TogglePaymentRemarksModalComponent;
    const onSuccess = content.onSuccess;
    onSuccess
      .pipe(
        tap((data) => {
          this._triggerSensitiveDataAccess$.next(data);
          if (action === 'enable') {
            this.accessGranted.emit();
          }
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }
}
